import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Status = 'healthy' | 'degraded' | 'depleted' | 'unknown';

interface AiGatewayHealth {
  status: Status;
  lastDepletedAt: string | null;
  dismissed: boolean;
  dismiss: () => void;
}

const Ctx = createContext<AiGatewayHealth>({
  status: 'unknown',
  lastDepletedAt: null,
  dismissed: false,
  dismiss: () => {},
});

const DISMISS_KEY = 'ai-gateway-banner-dismissed-at';
const POLL_MS = 60_000;

export function AiGatewayHealthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('unknown');
  const [lastDepletedAt, setLastDepletedAt] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-gateway-monitor?action=status`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
      });
      if (!res.ok) return;
      const json = await res.json();
      setStatus(json.status ?? 'healthy');
      setLastDepletedAt(json.lastDepletedAt ?? null);

      // Clear dismissal if a fresh depletion arrived after the dismissal
      if (json.lastDepletedAt) {
        const dismissedAt = sessionStorage.getItem(DISMISS_KEY);
        if (dismissedAt && new Date(json.lastDepletedAt) > new Date(dismissedAt)) {
          sessionStorage.removeItem(DISMISS_KEY);
          setDismissed(false);
        }
      }
    } catch {
      // silent — never break the app
    }
  }, []);

  useEffect(() => {
    // Restore dismissal from session
    if (sessionStorage.getItem(DISMISS_KEY)) setDismissed(true);
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setDismissed(true);
  }, []);

  return (
    <Ctx.Provider value={{ status, lastDepletedAt, dismissed, dismiss }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAiGatewayHealth = () => useContext(Ctx);
